import { marked } from 'marked'
import DOMPurify from 'dompurify'

interface MarkdownProcessorOptions {
  /** Número máximo de caracteres. Si se especifica, el texto se truncará a esta longitud */
  limit?: number
  /** Configuración para DOMPurify */
  sanitizeOptions?: DOMPurify.Config
  /** Si es true, no parsea el texto como markdown, solo normaliza los saltos de línea */
  rawText?: boolean
}

/**
 * Procesa texto markdown: normaliza saltos de línea, convierte a HTML, limita longitud y sanitiza
 * @param text Texto markdown a procesar
 * @param options Opciones de procesamiento
 * @returns HTML procesado y sanitizado (o texto normalizado si rawText=true)
 */
export function processMarkdown(text: string, options: MarkdownProcessorOptions = {}): string {
  if (!text) return ''

  // 1. Normalizar saltos de línea
  let processedText = text
    .replace(/\\n\\n/g, '\n\n') // Primero reemplazar \n\n para no interferir
    .replace(/\\n/g, '\n') // Luego reemplazar \n individuales

  // 2. Asegurar que hay líneas en blanco antes de listas numeradas
  processedText = processedText.replace(/([^\n])\n(\d+\.)/g, '$1\n\n$2')

  // 3. Aplicar límite si está configurado
  if (options.limit && processedText.length > options.limit) {
    processedText = processedText.substring(0, options.limit) + '...'
  }

  // 4. Si solo queremos el texto normalizado, retornamos aquí
  if (options.rawText) {
    return processedText
  }

  // 5. Convertir markdown a HTML de forma sincrónica
  const rawHTML = marked.parse(processedText, { async: false }) as string

  // 6. Sanitizar el HTML resultante
  return DOMPurify.sanitize(rawHTML, options.sanitizeOptions ?? {}) as string
}
